import React from "react";
import { Pencil, Sparkles, Download, Trash2, Copy } from "lucide-react";
import "../../../styles/resume-tile.css";

interface ResumeTileProps {
  id: string;
  title: string;
  createdAt: string;
  updatedAt?: string | null;
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onTailorToJob?: (id: string) => void;
  onDownload?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const formatLastEdited = (createdAt: string, updatedAt?: string | null) => {
  const dateStr = updatedAt || createdAt;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays <= 0) return "Last Edited: today";
  if (diffDays === 1) return "Last Edited: 1 day ago";
  if (diffDays < 7) return `Last Edited: ${diffDays} days ago`;

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) return "Last Edited: 1 week ago";
  if (diffWeeks < 5) return `Last Edited: ${diffWeeks} weeks ago`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return "Last Edited: 1 month ago";
  return `Last Edited: ${diffMonths} months ago`;
};

export const ResumeTile: React.FC<ResumeTileProps> = ({
  id,
  title,
  createdAt,
  updatedAt,
  onOpen,
  onEdit,
  onTailorToJob,
  onDownload,
  onDuplicate,
  onDelete,
}) => {
  const lastEditedLabel = formatLastEdited(createdAt, updatedAt);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(id);
  };

  const handleTailorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTailorToJob) onTailorToJob(id);
  };

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDownload) onDownload(id);
  };

  const handleDuplicateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDuplicate) onDuplicate(id);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) onDelete(id);
  };

  const actionClass = (enabled: boolean) =>
    `resume-tile__action ${enabled ? "" : "resume-tile__action--disabled"}`;

  return (
    <div className="resume-tile" onClick={() => onOpen(id)}>
      <div className="resume-tile__card">
        <div className="resume-tile__preview">
          <div className="resume-tile__preview-inner">
            <div className="resume-tile__preview-header" />
            <div className="resume-tile__preview-lines">
              <span className="resume-tile__preview-line resume-tile__preview-line--short" />
              <span className="resume-tile__preview-line" />
              <span className="resume-tile__preview-line" />
              <span className="resume-tile__preview-line resume-tile__preview-line--short" />
            </div>
            <div className="resume-tile__preview-footer" />
          </div>
          {lastEditedLabel && (
            <div className="resume-tile__footer resume-tile__footer--preview">
              <span className="resume-tile__last-edited">{lastEditedLabel}</span>
            </div>
          )}
        </div>

        <div className="resume-tile__body">
          <div className="resume-tile__header">
            <h3 className="resume-tile__title" title={title}>
              {title}
            </h3>
            <p className="resume-tile__subtitle">Base Resume</p>
          </div>

          <div className="resume-tile__divider" />

          <div className="resume-tile__actions">
            <button
              type="button"
              className={actionClass(true)}
              onClick={handleEditClick}
            >
              <Pencil size={14} />
              <span>Edit Resume</span>
            </button>
            <button
              type="button"
              className={actionClass(!!onTailorToJob)}
              onClick={handleTailorClick}
              disabled={!onTailorToJob}
            >
              <Sparkles size={14} />
              <span>Tailor to Job</span>
            </button>
            <button
              type="button"
              className={actionClass(true)}
              onClick={handleDownloadClick}
            >
              <Download size={14} />
              <span>Download</span>
            </button>
            <button
              type="button"
              className={actionClass(true)}
              onClick={handleDuplicateClick}
            >
              <Copy size={14} />
              <span>Duplicate</span>
            </button>
            <button
              type="button"
              className={actionClass(true)}
              onClick={handleDeleteClick}
            >
              <Trash2 size={14} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeTile;


